import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Select,
  MenuItem,
  Box,
  TextField,
  InputAdornment,
  Button,
  Link,
  FormControlLabel,
  Switch,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ApartmentIcon from '@mui/icons-material/Apartment';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getHospitalId, getEmail, clearSessionAndStore } from '../Utils/authUtils';
import { AppDispatch } from '../store/store';
import { loginApi, Payload as LoginPayload, saveDeviceToken } from '../rtk-apis/auth/login';
import { verifyLoginMfaApi } from '../rtk-apis/auth/verifyLoginMfa';
import Loader from './Loader';
import { cardiacTokens as T } from '../theme/cardiacTheme';

/* ════════════════════════════════════════════════════════════════════════
   Switch Hospital — single MUI dialog on the shared cardiac theme.
   Merges the old two-step flow (select-hospital modal → separate password
   modal) into ONE dialog: pick a hospital, then the password field reveals
   inline; Submit re-authenticates against that hospital. When the account
   needs MFA, /login answers 202 + mfaRequired (no token) and the dialog
   asks for the emailed code, same as the login page.
   ════════════════════════════════════════════════════════════════════════ */

/** Default landing route per role (duplicated from routes/index.tsx to avoid a circular import). */
const getDefaultRouteForRole = (role: string): string => {
  if (role === 'SUPER_ADMIN' || role === 'System Admin') {
    return '/clinics';
  }
  if (role === 'Sonographer') {
    return '/';
  }
  if (role === 'Admin') {
    return '/dashboard';
  }
  return '/reports';
};

interface Hospital {
  _id: string;
  name: string;
  [k: string]: any;
}
interface Props {
  data?: Hospital[];
  isOpen: boolean;
  handleCloseModal: () => void;
}

const ChangeHospitalModal: React.FC<Props> = ({ data, isOpen, handleCloseModal }) => {
  const [selectedHospital, setSelectedHospital] = useState<Hospital | null>(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [mfaChallenge, setMfaChallenge] = useState<{
    challengeId: string;
    maskedEmail: string;
    expiresAt: string;
  } | null>(null);
  const [otp, setOtp] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  // Reset all local state whenever the dialog closes.
  useEffect(() => {
    if (!isOpen) {
      setSelectedHospital(null);
      setPassword('');
      setShowPassword(false);
      setErrorMessage('');
      setLoading(false);
      setMfaChallenge(null);
      setOtp('');
      setTrustDevice(false);
    }
  }, [isOpen]);

  const currentHospitalId = getHospitalId();
  const filteredHospitals = Array.isArray(data)
    ? data.filter(hospital => hospital._id !== currentHospitalId)
    : [];

  const handleSelectChange = event => {
    const hospitalId = event.target.value;
    setSelectedHospital(filteredHospitals.find(h => h._id === hospitalId) || null);
    setErrorMessage('');
    setPassword('');
  };

  const handleForgotPassword = () => {
    if (selectedHospital) {
      navigate('/forgotPassword', { state: { hospitalId: selectedHospital._id } });
    }
    handleCloseModal();
  };

  const applySession = (loginResult: any) => {
    const token = loginResult?.accessToken;
    if (!token) {
      setErrorMessage('Login failed: No token received.');
      return;
    }
    const useLocalStorage = !!localStorage.getItem('token');

    // Login OK — now clear old session + Redux and store the new context.
    clearSessionAndStore(dispatch);
    const storage = useLocalStorage ? localStorage : sessionStorage;
    const dataResult = loginResult?.data;
    const role = dataResult?.role;

    storage.setItem('token', token);
    if (loginResult?.refreshToken) {
      storage.setItem('refreshToken', loginResult.refreshToken);
    }
    storage.setItem('role', role);
    storage.setItem('userId', dataResult?._id);
    storage.setItem('hospitalId', dataResult?.hospitalId || dataResult?._id);
    storage.setItem('type', dataResult?.type);
    storage.setItem('pacsDetails', JSON.stringify(loginResult?.pacsResult || {}));

    if (role === 'Admin') {
      storage.setItem('name', dataResult?.adminDetails?.name || dataResult?.name);
      storage.setItem('email', dataResult?.adminDetails?.email || dataResult?.email);
    } else {
      storage.setItem('name', dataResult?.name);
      storage.setItem('email', dataResult?.email);
    }
    storage.setItem('loggedinEmail', dataResult?.adminDetails?.email || dataResult?.email);

    let hospitalName =
      role === 'Admin'
        ? dataResult?.name || selectedHospital?.name || dataResult?.clinicName
        : selectedHospital?.name || dataResult?.clinicName || dataResult?.name;
    if (!hospitalName || hospitalName === 'undefined') {
      hospitalName = 'Unknown Clinic';
    }
    storage.setItem('hospitalName', hospitalName);

    // Hard navigation — switching hospitals changes the entire user context, so
    // components must re-mount and re-fetch (a same-route SPA navigate wouldn't).
    window.location.href = getDefaultRouteForRole(role || '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedHospital?._id || !selectedHospital?.name) {
      setErrorMessage('Please select a clinic first.');
      return;
    }
    // Capture email BEFORE clearing storage (getEmail reads from storage).
    const email = getEmail();
    if (!email) {
      setErrorMessage('Session expired. Please login again.');
      return;
    }
    const payload: LoginPayload = {
      email: email.toLowerCase(),
      password,
      hospitalId: selectedHospital._id,
    };

    try {
      setLoading(true);
      setErrorMessage('');

      // Authenticate FIRST — if the password is wrong we jump to catch with storage untouched.
      const loginResult = await dispatch(loginApi(payload)).unwrap();

      // Password matched, but the account still needs the emailed code.
      if (loginResult?.mfaRequired) {
        setMfaChallenge({
          challengeId: loginResult.challengeId,
          maskedEmail: loginResult.email,
          expiresAt: loginResult.expiresAt,
        });
        setOtp('');
        return;
      }

      applySession(loginResult);
    } catch (error: any) {
      setErrorMessage(error?.msg || error?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaChallenge || otp.length !== 6) return;
    try {
      setLoading(true);
      setErrorMessage('');
      const result = await dispatch(
        verifyLoginMfaApi({ challengeId: mfaChallenge.challengeId, otp, trustDevice })
      ).unwrap();
      if (result?.deviceToken) {
        saveDeviceToken(result.deviceToken);
      }
      applySession(result);
    } catch (error: any) {
      setErrorMessage(error?.msg || error?.message || 'Invalid or expired verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackFromOtp = () => {
    setMfaChallenge(null);
    setOtp('');
    setTrustDevice(false);
    setErrorMessage('');
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCloseModal}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          background: `linear-gradient(180deg, ${T.surface2}, ${T.surface})`,
          border: `1px solid ${T.lineStrong}`,
          borderRadius: '16px',
          boxShadow: '0 30px 70px -24px rgba(0,0,0,0.85)',
          color: T.ink,
        },
      }}
      BackdropProps={{ sx: { backgroundColor: 'rgba(0,16,31,0.72)', backdropFilter: 'blur(3px)' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          m: 0,
          px: '22px',
          pt: '20px',
          pb: '14px',
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '10px',
            display: 'grid',
            placeItems: 'center',
            color: T.teal,
            background: 'rgba(45,212,191,0.12)',
            border: '1px solid rgba(45,212,191,0.35)',
          }}
        >
          <ApartmentIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography
          component="span"
          sx={{ fontSize: 18, fontWeight: 700, color: '#fff' }}
        >
          Switch Clinic
        </Typography>
        <IconButton
          onClick={handleCloseModal}
          aria-label="Close"
          sx={{
            ml: 'auto',
            color: T.ink3,
            '&:hover': { color: T.ink, background: 'rgba(255,255,255,0.06)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: '22px', pb: '24px' }}>
        {mfaChallenge ? (
          <Box
            component="form"
            onSubmit={handleVerifyOtp}
            sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            <Typography sx={{ fontSize: 14, color: T.ink2 }}>
              We sent a 6-digit code to {mfaChallenge.maskedEmail}. It expires at{' '}
              {new Date(mfaChallenge.expiresAt).toLocaleTimeString()}.
            </Typography>
            <TextField
              fullWidth
              autoFocus
              placeholder="123456"
              value={otp}
              onChange={e => {
                setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                if (errorMessage) {
                  setErrorMessage('');
                }
              }}
              disabled={loading}
              error={!!errorMessage}
              helperText={errorMessage || ' '}
              inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: 6, fontSize: 20 } }}
              sx={{
                '& .MuiOutlinedInput-root': { background: T.surface, borderRadius: '10px', color: T.ink },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: T.line },
                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.teal },
                '& .MuiFormHelperText-root': { color: T.red, ml: '2px' },
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={trustDevice}
                  onChange={() => setTrustDevice(t => !t)}
                  disabled={loading}
                />
              }
              label="Trust this device for 30 days"
              sx={{ m: 0, '& .MuiFormControlLabel-label': { color: T.ink2, fontSize: 13 } }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || otp.length !== 6}
              sx={{
                color: '#04221d',
                fontWeight: 700,
                borderRadius: '10px',
                py: '10px',
                background: `linear-gradient(92deg, ${T.sky}, ${T.teal})`,
                '&:hover': { filter: 'brightness(1.08)', background: `linear-gradient(92deg, ${T.sky}, ${T.teal})` },
                '&.Mui-disabled': { opacity: 0.5, color: '#04221d' },
              }}
            >
              {loading ? 'Verifying…' : 'Verify and switch'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                onClick={handleBackFromOtp}
                underline="hover"
                sx={{ fontSize: 13, color: T.sky, cursor: 'pointer' }}
              >
                Back
              </Link>
            </Box>
          </Box>
        ) : filteredHospitals.length > 0 ? (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  color: T.ink3,
                  mb: '8px',
                }}
              >
                Select a clinic to switch to
              </Typography>
              <Select
                fullWidth
                displayEmpty
                value={selectedHospital?._id || ''}
                onChange={handleSelectChange}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      background: T.surface2,
                      border: `1px solid ${T.lineStrong}`,
                      mt: '4px',
                      '& .MuiMenuItem-root': {
                        fontSize: 14,
                        color: T.ink2,
                        '&:hover': { background: 'rgba(45,212,191,0.12)', color: T.teal },
                      },
                    },
                  },
                }}
                sx={{
                  background: T.surface,
                  borderRadius: '10px',
                  color: T.ink,
                  fontSize: 14,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: T.line },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.lineStrong },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.teal },
                  '& .MuiSelect-icon': { color: T.ink3 },
                }}
              >
                <MenuItem
                  value=""
                  disabled
                  sx={{ color: T.ink3 }}
                >
                  --Select--
                </MenuItem>
                {filteredHospitals.map((hospital, index) => (
                  <MenuItem
                    key={hospital._id || index}
                    value={hospital._id}
                  >
                    {hospital.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Password reveals once a hospital is chosen — one dialog, no second popup. */}
            {selectedHospital && (
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    color: T.ink3,
                    mb: '8px',
                  }}
                >
                  Enter password for {selectedHospital.name}
                </Typography>
                <TextField
                  fullWidth
                  autoFocus
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={password}
                  onChange={e => {
                    setPassword(e.target.value);
                    if (errorMessage) {
                      setErrorMessage('');
                    }
                  }}
                  disabled={loading}
                  error={!!errorMessage}
                  helperText={errorMessage || ' '}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ fontSize: 18, color: T.ink3 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(s => !s)}
                          edge="end"
                          size="small"
                          sx={{ color: T.ink3 }}
                        >
                          {showPassword ? (
                            <VisibilityOff sx={{ fontSize: 18 }} />
                          ) : (
                            <Visibility sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: T.surface,
                      borderRadius: '10px',
                      color: T.ink,
                      fontSize: 14,
                    },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: T.line },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.lineStrong },
                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.teal },
                    '& .MuiFormHelperText-root': { color: T.red, ml: '2px' },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || !password.trim()}
                  sx={{
                    mt: '4px',
                    color: '#04221d',
                    fontWeight: 700,
                    borderRadius: '10px',
                    py: '10px',
                    background: `linear-gradient(92deg, ${T.sky}, ${T.teal})`,
                    boxShadow: '0 6px 16px -7px rgba(45,212,191,0.7)',
                    '&:hover': {
                      filter: 'brightness(1.08)',
                      background: `linear-gradient(92deg, ${T.sky}, ${T.teal})`,
                    },
                    '&.Mui-disabled': { opacity: 0.5, color: '#04221d' },
                  }}
                >
                  {loading ? 'Switching…' : 'Switch Clinic'}
                </Button>

                <Box sx={{ textAlign: 'right', mt: '10px' }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={handleForgotPassword}
                    underline="hover"
                    sx={{ fontSize: 13, color: T.sky, cursor: 'pointer' }}
                  >
                    Forgot password?
                  </Link>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 14, color: T.ink2 }}>No other clinics available.</Typography>
        )}
      </DialogContent>

      <Loader isLoading={loading} />
    </Dialog>
  );
};

export default ChangeHospitalModal;
